import { useAppTranslation } from '../i18n/useAppTranslation';
import { Box, Paper, Typography, Chip, Grid } from '@mui/material';
import { format, parseISO } from 'date-fns';


/**
 * Deux jauges circulaires cote sportif :
 *  - « Score de la dernière séance »   : score global de la derniere seance notee ;
 *  - « Score moyen des séances »   : moyenne de toutes les seances notees.
 *
 * Extrait en composant pour etre affiche a l'identique dans plusieurs onglets
 * (Tableau de bord + Performances) sans dupliquer le code.
 */
type Props = { latestPerformance: any; performances: any[]; isMobile?: boolean };

const Gauge = ({ score, subtitle }: { score: number; subtitle: string }) => {
  const { tr, locale } = useAppTranslation();
  const pct = Math.round(score); // score sur /100 en interne
  const color = pct >= 70 ? '#22C55E' : pct >= 45 ? '#F59E0B' : '#EF4444';
  const label = pct >= 70 ? 'Excellent' : pct >= 45 ? 'Correct' : 'À améliorer';
  const r = 80;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative', width: 200, height: 200 }}>
        <svg width="200" height="200" viewBox="0 0 200 200">
          <circle cx="100" cy="100" r={r} fill="none" stroke="#e5e7eb" strokeWidth="16" />
          <circle
            cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="16"
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeLinecap="round"
            transform="rotate(-90 100 100)"
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        </svg>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={900} sx={{ color, lineHeight: 1 }}>
            {(score / 10).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
          </Typography>
          <Typography variant="caption" color="text.secondary">/10</Typography>
        </Box>
      </Box>
      <Chip label={tr(label)} sx={{ bgcolor: color, color: 'white', fontWeight: 700, fontSize: '0.9rem', px: 1 }} />
      <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
    </Box>
  );
};

export default function PerformanceGauges({ latestPerformance, performances, isMobile }: Props) {
  const { tr, dateLocale } = useAppTranslation();
  const avg = performances.length > 0
    ? performances.reduce((s, p) => s + (p.global_score || 0), 0) / performances.length
    : 0;
  const n = performances.length;

  const paperSx = {
    p: isMobile ? 1.5 : 3, borderRadius: 4, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', minHeight: 280,
  } as const;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Paper variant="outlined" sx={{ ...paperSx, height: '100%' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Score de la dernière séance")}</Typography>
          {latestPerformance ? (
            <Gauge
              score={latestPerformance.global_score || 0}
              subtitle={`${tr("Séance du")} ${format(parseISO(latestPerformance.date), 'dd MMMM yyyy', { locale: dateLocale })}`}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">{tr("Aucune séance notée pour le moment.")}</Typography>
          )}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Paper variant="outlined" sx={{ ...paperSx, height: '100%' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Score moyen des séances")}</Typography>
          {n > 0 ? (
            <Gauge score={avg} subtitle={tr("Moyenne sur {count} séances notées", { count: n })} />
          ) : (
            <Typography variant="body2" color="text.secondary">{tr("Aucune séance notée pour le moment.")}</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
